import React, { useState } from "react";
import { Link } from "gatsby";
import { getImageFromDrive, saveSearchFeedback } from "../../services/api";
import { toast } from "react-toastify";
import { RxImage } from "react-icons/rx";

const parseWidth = (text) => {
  if (!text) return null;
  const match = String(text).match(/Width\s*:\s*([^\n]+)/i);
  return match ? match[1].trim() : null;
};

const parseTags = (product) => {
  const raw = [
    product.material,
    product.constructions,
    product.finish,
    product.functions,
  ];

  return raw
    .filter(Boolean)
    .flatMap((value) => String(value).split(/[,/]/))
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0 && tag.toLowerCase() !== "0")
    .slice(0, 6);
};

const NEGATIVE_TAGS = [
  "Not what I expected",
  "Too heavy",
  "Wrong color",
  "Wrong material",
  "Show me similar options",
];

const getFeedbackLabel = (feedback) => {
  if (!feedback) return null;

  const { feedbackType, comment, suggestedChange } = feedback;
  const text = suggestedChange || comment;

  if (feedbackType === "positive") {
    return "You liked this result";
  }

  if (text) {
    return `Feedback: ${text}`;
  }

  if (feedbackType === "negative") {
    return "You didn't find this useful";
  }

  return "Feedback submitted";
};

const ImageSearchSuggestion = ({
  product,
  score,
  matchReason,
  matchPoints,
  historyId,
  token,
  isLoggedIn,
  existingFeedback,
}) => {
  const {
    productsid,
    image,
    articlenumber,
    fabricname,
    finish,
    functions,
    weightnumber,
    productdetail,
  } = product;

  const productId = String(productsid || "").replace(/[^a-zA-Z0-9-_]/g, "");
  const width = parseWidth(productdetail);
  const tags = parseTags(product);
  const points = Array.isArray(matchPoints) ? matchPoints : [];
  const reasonText = matchReason || (points[0] ? points[0] : "");

  const [feedbackType, setFeedbackType] = useState(null);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const submitFeedback = async (type, quickComment = "") => {
    if (!token || !historyId) return;

    const finalComment = quickComment || comment;
    const result = await saveSearchFeedback(token, {
      imageSearchHistory: historyId,
      product: product.id,
      feedbackType: type,
      comment: finalComment,
      suggestedChange: quickComment,
    });

    if (result.data) {
      setSubmitted(true);
      toast.success("Feedback saved");
    } else {
      toast.error("Failed to save feedback");
    }
  };

  const getScoreClass = (value) => {
    if (value >= 80) return "score-high";
    if (value >= 60) return "score-medium";
    return "score-low";
  };

  return (
    <div className="image-suggestion-card">
      <Link
        to={`/products/${productId}`}
        className="image-suggestion-card__link"
      >
        <div className="image-suggestion-card__image-wrapper">
          {image && image !== "0" && image !== "#N/A" ? (
            <img
              referrerPolicy="no-referrer"
              src={getImageFromDrive(image, "400")}
              alt={articlenumber || "Product image"}
              className="image-suggestion-card__image"
            />
          ) : (
            <div className="image-suggestion-card__no-image">
              <RxImage />
            </div>
          )}

          {typeof score === "number" && (
            <div
              className={`image-suggestion-card__score ${getScoreClass(score)}`}
            >
              {score}% match
            </div>
          )}
        </div>

        <div className="image-suggestion-card__info">
          {articlenumber && (
            <p className="image-suggestion-card__article">{articlenumber}</p>
          )}

          {fabricname && (
            <p className="image-suggestion-card__name">{fabricname}</p>
          )}

          {reasonText && (
            <p className="image-suggestion-card__reason-line">{reasonText}</p>
          )}

          {tags.length > 0 && (
            <div className="image-suggestion-card__tags">
              {tags.map((tag, index) => (
                <span key={index} className="attribute-tag">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="image-suggestion-card__details">
            {weightnumber && (
              <div className="detail-row">
                <span className="detail-label">Weight:</span>
                <span className="detail-value">{weightnumber} g/m²</span>
              </div>
            )}

            {width && (
              <div className="detail-row">
                <span className="detail-label">Width:</span>
                <span className="detail-value">{width}</span>
              </div>
            )}

            {finish && (
              <div className="detail-row">
                <span className="detail-label">Finish:</span>
                <span className="detail-value">{finish}</span>
              </div>
            )}

            {functions && (
              <div className="detail-row">
                <span className="detail-label">Features:</span>
                <span className="detail-value">{functions}</span>
              </div>
            )}
          </div>

          {points.length > 0 && (
            <div className="image-suggestion-card__why-match">
              <p className="why-match__title">Why it&apos;s a close match</p>
              <ul className="why-match__list">
                {points.map((point, index) => (
                  <li key={index} className="why-match__item">
                    ✓ {point}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Link>

      {existingFeedback ? (
        <div className="image-suggestion-card__feedback image-suggestion-card__feedback--readonly">
          <p className="feedback-readonly-text">
            {getFeedbackLabel(existingFeedback)}
          </p>
        </div>
      ) : (
        isLoggedIn &&
        !submitted && (
          <div className="image-suggestion-card__feedback">
            {!feedbackType ? (
              <div className="feedback-actions feedback-actions--text">
                <button
                  type="button"
                  className="feedback-text-btn feedback-text-btn--positive"
                  onClick={() => submitFeedback("positive", "Keep this")}
                >
                  Keep this
                </button>
                <button
                  type="button"
                  className="feedback-text-btn feedback-text-btn--negative"
                  onClick={() => setFeedbackType("negative")}
                >
                  Not quite right
                </button>
              </div>
            ) : (
              <div className="feedback-panel">
                <p className="feedback-panel__hint">
                  Tell us why this isn&apos;t right:
                </p>
                <div className="feedback-tags">
                  {NEGATIVE_TAGS.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      className="feedback-tag-btn"
                      onClick={() => submitFeedback("negative", tag)}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                <textarea
                  className="feedback-comment"
                  placeholder="Add more details (optional)"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={2}
                />
                <div className="feedback-panel__actions">
                  <button
                    type="button"
                    className="feedback-submit"
                    onClick={() => submitFeedback("negative")}
                  >
                    Send feedback
                  </button>
                  <button
                    type="button"
                    className="feedback-cancel"
                    onClick={() => {
                      setFeedbackType(null);
                      setComment("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      )}

      {isLoggedIn && submitted && !existingFeedback && (
        <p className="feedback-thanks">
          Thanks, we&apos;ll use this to improve your results.
        </p>
      )}
    </div>
  );
};

export default ImageSearchSuggestion;
